import React, { FC, forwardRef, ReactNode, ElementType } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionProps,
  AccordionSummaryProps,
  AccordionDetailsProps,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export interface FooterAccordionProps extends Omit<AccordionProps, 'variant'> {
  /**
   * Label of the Accordion
   */
  label?: ReactNode;
  /**
   * Props applied to the wrapper of label
   */
  accordionSummaryProps?: AccordionSummaryProps<ElementType>;
  /**
   * Props applied to the content of the accodion
   */
  accordionDetailsProps?: AccordionDetailsProps;
  /**
   * Properties applied to wrapper of children
   */
  contentProps?: TypographyProps<ElementType>;
  /**
   * Variant of accordion
   * @default compact
   */
  variant?: 'compact' | 'standard';
}

/**
 * Collapsible footer section
 */
export const FooterAccordion: FC<FooterAccordionProps> = forwardRef(
  (
    {
      sx,
      children,
      className,
      label,
      accordionSummaryProps,
      accordionDetailsProps,
      contentProps,
      variant = 'compact',
      ...rest
    },
    ref,
  ) => {
    const { sx: accordionSummarySx, ...restAccordionSummary } = accordionSummaryProps ?? {};
    const theme = useTheme();

    return (
      <Accordion
        ref={ref}
        data-testid="FooterAccordion"
        className={[
          'FooterAccordion-root',
          `FooterAccordion-variant${variant.charAt(0).toUpperCase()}${variant.slice(1)}`,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        sx={{
          // recolor to dark mode
          color: 'currentColor',
          ...(variant === 'compact' && {
            border: 'none',
          }),
          ...sx,
        }}
        {...rest}
      >
        <AccordionSummary
          data-testid="FooterAccordion.label"
          aria-controls="footer-accordion"
          id="footer-accordion"
          sx={{
            // recolor to dark mode
            color: 'currentColor',
            '&:hover': {
              color: 'currentColor',
              // hover over IconButton
              '& .MuiAccordion-IconButton': {
                backgroundColor: theme.palette.transparent.tertiary,
              },
            },
            // change variant of icon button to small & white
            '& .MuiAccordion-IconButton': {
              height: '32px',
              width: '32px',
              ...getInteractiveStyle(
                {
                  backgroundColor: 'transparent',
                  color: theme.palette.onSurface.os0,
                },
                {
                  color: theme.palette.onSurface.os0,
                  backgroundColor: theme.palette.transparent.tertiary,
                },
              ),
            },
            ...(variant === 'compact' && {
              ...theme.typography.caption2,
              p: '0',
              gap: 1.5,
              justifyContent: 'flex-start',
              // move icon next to label
              '& .MuiAccordionSummary-content': {
                flexGrow: '0',
              },
            }),
            ...accordionSummarySx,
          }}
          {...restAccordionSummary}
        >
          {label}
        </AccordionSummary>
        <AccordionDetails data-testid="FooterAccordion.children" {...accordionDetailsProps}>
          <Typography variant="disclaimer1" {...contentProps}>
            {children}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  },
);
